import { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { DataTable, DataTableFilterMeta, DataTableSelectionSingleChangeEvent } from 'primereact/datatable';
import { Column, ColumnEditorOptions, ColumnSortEvent } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { RawMaterialGroupService } from '../../service/RawMaterialGroupService';
import RawMaterialGroup from '../../models/RawMaterialGroup';
import { Link } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';

const RawMaterialGroups = () => {
    let emptyRawMaterialGroup: RawMaterialGroup = {
        id: 0,
        raw_Material_Group: '',
        description: '',
        isExcluded: false,
        isUsed: false,
        hasColorCodes: false
    };

    const [rawMaterialGroups, setRawMaterialGroups] = useState<RawMaterialGroup[]>([]);
    const [loadingRawMaterialGroups, setLoadingRawMaterialGroups] = useState(true);
    const [deleteRawMaterialGroupDialog, setDeleteRawMaterialGroupDialog] = useState(false);
    const [rawMaterialGroup, setRawMaterialGroup] = useState<any>(emptyRawMaterialGroup);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [editingRows, setEditingRows] = useState({});
    const isNew = useRef(false);
    const toast = useRef<any>(null);
    const dtRawMaterialGroups = useRef<any>(null);
    const itRawMaterialGroup = useRef<any>(null);
    const itRawMaterialGroupDescription = useRef<any>(null);
    const chkIsExcluded = useRef<any>(null);
    
    useEffect(() => {
        RawMaterialGroupService.getRawMaterialGroups().then(data => {
            setRawMaterialGroups(data);
            setLoadingRawMaterialGroups(false);
        });
    }, []);

    const {
        control: controlRawMaterialGroup, 
        formState: { errors: errorsRawMaterialGroup }, 
        reset: resetRawMaterialGroup,
        handleSubmit: handleSubmitRawMaterialGroup } = useForm();

    const formEditRawMaterialGroup = useForm({ mode: "all" });

    const onSubmitRawMaterialGroup = (data: any) => {
        isNew.current = true;
        let _rawMaterialGroup = { ...rawMaterialGroup };
        _rawMaterialGroup.raw_Material_Group = data.rawMaterialGroup;
        _rawMaterialGroup.description = data.description;
        _rawMaterialGroup.isExcluded = data.isExcluded;
        saveRawMaterialGroup(_rawMaterialGroup);
    };

    const getFormErrorMessage = (name: string) => {
        return errorsRawMaterialGroup[name] ? <small className="p-error">{(errorsRawMaterialGroup[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const hideDeleteRawMaterialGroupDialog = () => {
        setDeleteRawMaterialGroupDialog(false);
    };

    const saveRawMaterialGroup = (rawMaterialGroupData: RawMaterialGroup) => {
        let _rawMaterialGroups = [...rawMaterialGroups];

        if (isNew.current) {
            RawMaterialGroupService.addRawMaterialGroup(rawMaterialGroupData).then((data) => {
                if (data) {
                    _rawMaterialGroups.push(data);
                    setRawMaterialGroups(_rawMaterialGroups);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Raw Material Group Created', life: 3000 });
                    setRawMaterialGroup(emptyRawMaterialGroup);
                    _rawMaterialGroups.sort(function (a, b) { return (a.raw_Material_Group < b.raw_Material_Group) ? -1 : (a.raw_Material_Group < b.raw_Material_Group) ? 1 : 0; });
                    dtRawMaterialGroups.current.props.value = _rawMaterialGroups;
                    resetRawMaterialGroup();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Raw Material Group', life: 3000 });
                }
            });
        } else {
            RawMaterialGroupService.updateRawMaterialGroup(rawMaterialGroupData).then((data) => {
                if (data) {
                    let index = rawMaterialGroups.findIndex(r => r.id === rawMaterialGroupData.id);
                    _rawMaterialGroups[index] = rawMaterialGroupData;
                    setRawMaterialGroups(_rawMaterialGroups);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Raw Material Group Updated', life: 3000 });
                    setRawMaterialGroup(emptyRawMaterialGroup);
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Update Raw Material Group', life: 3000 });
                }
            });
        }
    }

    const confirmDeleteRawMaterialGroup = (rawMaterialGroup: RawMaterialGroup) => {
        setRawMaterialGroup(rawMaterialGroup);
        setDeleteRawMaterialGroupDialog(true);
    }

    const deleteRawMaterialGroup = () => {
        RawMaterialGroupService.deleteRawMaterialGroup(rawMaterialGroup.id).then((data) => {
            if (data) {
                let _rawMaterialGroups = rawMaterialGroups.filter(val => val.id !== rawMaterialGroup.id);
                setRawMaterialGroups(_rawMaterialGroups);
                setDeleteRawMaterialGroupDialog(false);
                setRawMaterialGroup(emptyRawMaterialGroup);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Raw Material Group Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Delete Raw Material Group', life: 3000 });
            }
        });
    }

    const rawMaterialGroupSort = (e: ColumnSortEvent) => {
        e.data.sort((data1: RawMaterialGroup, data2: RawMaterialGroup) => {
            const value1 = data1.raw_Material_Group;
            const value2 = data2.raw_Material_Group;
            let result = null;

            if (value1 == null && value2 != null)
                result = -1;
            else if (value1 != null && value2 == null)
                result = 1;
            else if (value1 == null && value2 == null)
                result = 0;
            else if (typeof value1 === 'string' && typeof value2 === 'string')
                result = value1.localeCompare(value2, undefined, { numeric: false });
            else
                result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

            return ((e.order as number) * result);
        });
        return e.data;
    }

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }

    const setRawMaterialGroupActiveRowIndex = (rowData: DataTableSelectionSingleChangeEvent<RawMaterialGroup[]>) => {
        let _rawMaterialGroup = (rowData.value as RawMaterialGroup);
        let selectedRawMaterialGroupIndex = rawMaterialGroups.findIndex(v => v.id === _rawMaterialGroup.id);
        let _editingRows = { ...editingRows, ...{ [`${rawMaterialGroups[selectedRawMaterialGroupIndex].id}`]: true } };
        
        if(Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    }

    const rawMaterialGroupEditor = (options: any) => {
        return (
            <Controller 
                name="rawMaterialGroupEdit" 
                control={formEditRawMaterialGroup.control}
                defaultValue = {options.value}
                rules={{ 
                    required: 'Raw Material Group is required.',
                    validate: {
                        unique: v => rawMaterialGroups.filter(val => val.raw_Material_Group.localeCompare(v, 'en', {sensitivity: 'accent'}) === 0).length === 0 || 'Raw Material Group must be unique.'
                    }
                }}
                render={({ field, fieldState }) => {
                    rawMaterialGroup.raw_Material_Group = field.value;

                    return (
                        <>
                            <InputText ref={itRawMaterialGroup} value={fieldState.isDirty ? field.value : options.value} maxLength={10} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { rawMaterialGroup.raw_Material_Group = e.target.value; field.onChange(e.target.value);}} placeholder="Raw Material Group" />
                        </>
                    )}
                }
            />
        );
    }

    const descriptionEditor = (options: any) => {
        return (
            <Controller 
                name="descriptionEdit" 
                control={formEditRawMaterialGroup.control} 
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    rawMaterialGroup.description = field.value;

                    return (
                        <>
                            <InputText ref={itRawMaterialGroupDescription} value={fieldState.isDirty ? field.value : options.value} maxLength={50} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => {rawMaterialGroup.description = e.target.value; field.onChange(e.target.value);}} placeholder="Description" />
                        </>
                    )}
                }
            />
        );
    }

    const isExcludedEditor = (options: any) => {
        return (
            <Controller 
                name="isExcludedEdit" 
                control={formEditRawMaterialGroup.control} 
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    rawMaterialGroup.isExcluded = field.value;

                    return (
                        <>
                            <Checkbox ref={chkIsExcluded} inputId={field.name} checked={fieldState.isDirty ? field.value : options.value} inputRef={field.ref} className={classNames({ 'p-invalid mr-1': fieldState.error })} onChange={(e) => { rawMaterialGroup.isExcluded = e.target.value; field.onChange(e.checked) }} />
                        </>
                    )}
                }
            />
        );
    }

    const rawMaterialGroupExpanderTemplate = (rowData: RawMaterialGroup) => {
        return (
            <Link className="pi pi-angle-right" to={`/rawmaterialgroups/${rowData.id}`}></Link>
        )
    }
    
    const rawMaterialGroupBodyTemplate = (rowData: RawMaterialGroup) => {
        return rowData.raw_Material_Group;
    }

    const descriptionBodyTemplate = (rowData: RawMaterialGroup) => {
        return rowData.description;
    }

    const isExcludedBodyTemplate = (rowData: RawMaterialGroup) => {
        if (rowData.isExcluded) {
            return (
                <>
                    <i className="pi pi-check text-green-500"></i>
                </>
            );
        } else {
            return (
                <>
                    <i className="pi pi-times text-red-500"></i>
                </>
            );
        }
    }

    const rowEditorRawMaterialGroupsTemplate = (rowData: RawMaterialGroup, props: any) => {
        const rowEditor = props.rowEditor;

        if (rowEditor.editing) {
            isNew.current = false;

            return (
                <>
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => {
                        if (!formEditRawMaterialGroup.getFieldState("rawMaterialGroupEdit").invalid && !formEditRawMaterialGroup.getFieldState("descriptionEdit").invalid) {
                            let _rawMaterialGroup = { ...rawMaterialGroup };
                            _rawMaterialGroup.id = rowData.id;
                            _rawMaterialGroup.raw_Material_Group = itRawMaterialGroup.current.value;
                            _rawMaterialGroup.description = itRawMaterialGroupDescription.current.value;
                            _rawMaterialGroup.isExcluded = chkIsExcluded.current.props.checked;
                            saveRawMaterialGroup(_rawMaterialGroup);
                            rowEditor.onSaveClick(e);
                        }
                    }} />
                    
                    <Button icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        formEditRawMaterialGroup.clearErrors();
                        formEditRawMaterialGroup.reset();
                        rowEditor.onCancelClick(e);
                    }} />
                </>
            );
        } else {
            return (
                !rowData.isUsed && !rowData.hasColorCodes
                ?
                    <>
                        <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDeleteRawMaterialGroup(rowData)} to={''}></Link>
                    </>
                : <></>
            );
        }
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const headerRawMaterialGroupTable = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const deleteRawMaterialGroupDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRawMaterialGroupDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteRawMaterialGroup} />
        </>
    );

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Card className="layout-main-card border-noround">
                    <Panel header="Add Raw Material Group" className="addPanel mb-2">
                        <form key={1} onSubmit={handleSubmitRawMaterialGroup(onSubmitRawMaterialGroup)} className="p-fluid" autoComplete="off">
                            <div className="formgrid grid">
                                <div className="field col-3">
                                    <Controller
                                        name="rawMaterialGroup"
                                        control={controlRawMaterialGroup}
                                        defaultValue=""
                                        rules={{
                                            required: 'Raw Material Group is required.',
                                            validate: {
                                                unique: v => rawMaterialGroups.filter(val => val.raw_Material_Group.localeCompare(v, 'en', {sensitivity: 'accent'}) === 0).length === 0 || 'Raw Material Group must be unique.'
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Raw Material Group</label>
                                                <InputText id={field.name} name={field.name} value={field.value} maxLength={10} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.target.value)} placeholder="Raw Material Group" />
                                                {getFormErrorMessage('rawMaterialGroup')}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="field col-3">
                                    <Controller
                                        name="description"
                                        control={controlRawMaterialGroup}
                                        defaultValue=""
                                        rules={{
                                            required: 'Description is required.'
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Description</label>
                                                <InputText id={field.name} name={field.name} value={field.value} maxLength={50} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.target.value)} placeholder="Description" />
                                                {getFormErrorMessage('description')}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="field col-fixed isCheckbox">
                                    <Controller
                                        name="isExcluded"
                                        control={controlRawMaterialGroup}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Exclude</label><br />
                                                <Checkbox inputId={field.name} checked={field.value} inputRef={field.ref} className={classNames('col-offset-4', { 'p-invalid mr-1': fieldState.error })} onChange={(e) => field.onChange(e.checked)} />
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="field field-buttons col-2 pt-4">
                                    <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                                    <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={resetRawMaterialGroup}/>
                                </div>
                            </div>
                        </form>
                    </Panel>

                    <DataTable ref={dtRawMaterialGroups} value={rawMaterialGroups} selectionMode="single" onSelectionChange={setRawMaterialGroupActiveRowIndex}
                        dataKey="id" size="small" rowHover={true} loading={loadingRawMaterialGroups}
                        sortField="raw_Material_Group" sortOrder={1}
                        scrollable scrollHeight="calc(100vh - 25.1rem)"
                        paginator alwaysShowPaginator={false} rows={25} rowsPerPageOptions={[5, 10, 25]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} raw material groups"
                        editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                        filters={filters} emptyMessage="No raw material groups found." header={headerRawMaterialGroupTable}>
                        <Column body={rawMaterialGroupExpanderTemplate} style={{ width: '3em' }} />
                        <Column field="raw_Material_Group" header="Raw Material Group" sortable sortFunction={rawMaterialGroupSort} body={rawMaterialGroupBodyTemplate} editor={(options:ColumnEditorOptions) => rawMaterialGroupEditor(options)} align="center" alignHeader="center"></Column>
                        <Column field="description" header="Description" sortable body={descriptionBodyTemplate} editor={(options:ColumnEditorOptions) => descriptionEditor(options)} align="center" alignHeader="center"></Column>
                        <Column field="isExcluded" header="Exclude From BOM" sortable body={isExcludedBodyTemplate} editor={(options: ColumnEditorOptions) => isExcludedEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column rowEditor body={rowEditorRawMaterialGroupsTemplate} bodyStyle={{ textAlign: 'center' }}></Column>
                    </DataTable>
                </Card>

                <Dialog visible={deleteRawMaterialGroupDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteRawMaterialGroupDialogFooter} onHide={hideDeleteRawMaterialGroupDialog}>
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        {rawMaterialGroup && <span>Are you sure you want to delete <b>{rawMaterialGroup.raw_Material_Group}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        </div>
    );
}

export default RawMaterialGroups;